import type { RecordCorrectionDto } from '@repo/api-client';
import { z } from 'zod';

import { INSTANT_MALFORMED, isoInstantFrom } from '@/lib/local-instant';

import { isCorrectionReason } from './correction-reasons';

/**
 * What the correction form accepts, and the two things it holds onto between
 * one attempt and the next.
 *
 * Kept beside the screen rather than in `lib/field-schemas.ts` because nothing
 * else in the app holds these rules yet. `POST .../stock/deliveries` and `POST
 * .../stock/stocktakes` take a quantity under bounds that are *nearly* these —
 * a delivery line shares the floor of 1 and the ceiling of 1,000,000, a
 * stocktake count is floored at 0 because an empty shelf is a real count — so
 * when those forms land this is the fragment to lift, with the floor as its
 * parameter. Lifting it now would be inventing a shared rule from one consumer,
 * which `docs/ui-conventions.md` asks not to do; two of them disagreeing about
 * the ceiling later would be worse.
 */

/** The floor and the ceiling the route states, restated as the form's own. */
const MIN_QUANTITY = 1;
const MAX_QUANTITY = 1_000_000;

/**
 * How many distinct items one correction may name.
 *
 * The route's own bound, and reaching it is not a thing a person does by hand —
 * it is here so that a form which somehow grew past it says so where the lines
 * are, rather than earning a `400 VALIDATION_FAILED` naming an array property.
 */
const MAX_LINES = 500;

export const OUTLET_REQUIRED = 'Choose the outlet whose level is being corrected.';
export const REASON_REQUIRED = 'Choose why the level is changing.';
export const WHEN_REQUIRED = 'Say when this happened.';
export const ITEM_REQUIRED = 'Choose the item this line is about.';
export const QUANTITY_REQUIRED = 'Enter how many.';

/**
 * **The message a minus sign earns**, and the reason the field is checked here
 * rather than left to the server.
 *
 * A negative quantity is refused `422 STOCK_QUANTITY_INVALID`, which is final
 * and correct — but it arrives after the whole form has been sent, and the thing
 * it has to teach is not "that number is out of range". It is that the sign is
 * not the person's to give: `BREAKAGE`, `SPOILAGE` and `LOSS` remove and `FOUND`
 * adds, and the API refuses a signed figure precisely so that "two fewer" has
 * one spelling instead of two.
 */
export const QUANTITY_WHOLE =
  'A quantity is a whole number of items, digits only — no minus sign and no decimal point. Which way the goods went is the reason’s to say, not this field’s.';

export const QUANTITY_TOO_SMALL = 'A correction moves at least one item.';

export const QUANTITY_TOO_LARGE = `A correction line is at most ${new Intl.NumberFormat('en-IN').format(MAX_QUANTITY)} items.`;

export const LINES_REQUIRED = 'Add at least one item to correct.';

export const TOO_MANY_LINES = `One correction names at most ${String(MAX_LINES)} items. Record the rest as a second correction.`;

/**
 * The same item on two lines.
 *
 * Refused here as well as by the API (`STOCK_DUPLICATE_ITEM_IN_REQUEST`), and
 * the duplication is deliberate rather than a client second-guessing a server:
 * that refusal is raised in the validation pipe and cannot name *which* line
 * broke the rule, while this one is attached to the line somebody just picked.
 * The mapping for the server's code stays wired up regardless — a tab left open
 * while an item is retired is not the only way a body reaches the wire.
 *
 * It says to merge rather than to delete, matching the API's own copy: two lines
 * for one item are usually two real counts of it, and deleting one loses goods.
 */
export const DUPLICATE_ITEM =
  'This item is already on another line. One correction may name each item once — put the whole quantity on a single line.';

/** Digits and nothing else: no sign, no point, no exponent, no spaces. */
const WHOLE_NUMBER = /^\d+$/;

/**
 * A quantity, as a string, because that is what an `<Input>` holds.
 *
 * Deliberately **not** `z.coerce.number()`. Coercion reads `''` as `0` and `'2x'`
 * as `NaN`, so the empty field and the mistyped one both land on whichever
 * numeric rule happens to catch them — "a correction moves at least one item"
 * for a field nobody has filled in yet. Checking the text first keeps "you have
 * not answered", "that is not a number" and "that number is out of range" three
 * different sentences.
 */
const quantitySchema = z
  .string()
  .trim()
  .min(1, QUANTITY_REQUIRED)
  .regex(WHOLE_NUMBER, QUANTITY_WHOLE)
  .refine((digits) => Number(digits) >= MIN_QUANTITY, QUANTITY_TOO_SMALL)
  .refine((digits) => Number(digits) <= MAX_QUANTITY, QUANTITY_TOO_LARGE);

const lineSchema = z.object({
  catalogueItemId: z.string().min(1, ITEM_REQUIRED),
  quantity: quantitySchema,
});

/**
 * When it happened.
 *
 * Built from `lib/local-instant.ts`'s parser rather than from its
 * `localInstantSchema`, and only the *malformed* message is borrowed: that one
 * is about calendars and is true wherever a person types a date, while its
 * required message reads "choose the date and time to look up", which belongs to
 * the two screens that look something up. This field is a fact being recorded,
 * not a question being asked.
 */
const whenSchema = z
  .string()
  .trim()
  .min(1, WHEN_REQUIRED)
  .refine((local) => isoInstantFrom(local) !== null, INSTANT_MALFORMED);

/**
 * The whole form.
 *
 * **`reason` is a `string` here and a `CorrectionReason` on the wire**, and the
 * step between them is `isCorrectionReason`, not a cast. It has to be a string
 * in the form because "nothing chosen yet" is `''` and no member of a closed set
 * can stand for that; it is checked against the published set as well as for
 * emptiness, so the picker and the document parting company shows as a field
 * that refuses rather than as a body the server answers `STOCK_REASON_INVALID`.
 *
 * **No upper bound on `occurredAt`, deliberately.** The API accepts any instant
 * and a form stricter than the route it posts to is a rule invented on one
 * screen: the device's clock is not the server's, and refusing a moment a few
 * minutes ahead of a slow terminal would refuse a true answer. What orders
 * anything is the server's `recordedAt` (FR-009, R-508); this field is the human
 * fact beside it.
 */
export const correctionSchema = z
  .object({
    outletId: z.string().min(1, OUTLET_REQUIRED),
    // **`: boolean` is load-bearing.** `isCorrectionReason` is a type predicate,
    // and both handing it to `refine` directly and wrapping it in an arrow that
    // returns it narrow the schema's *output* to the four reasons — TypeScript
    // has inferred predicates for arrow functions since 5.5 — while its input
    // stays `string`. The form would then hold one type and the submit handler
    // receive another, a seam react-hook-form's three generics have to be told
    // about at every call site that touches the control. Stating the return type
    // keeps the schema's two ends the same shape; the narrowing happens once, in
    // the submit handler, where the impossible state is read rather than
    // asserted away.
    reason: z
      .string()
      .min(1, REASON_REQUIRED)
      .refine((value): boolean => isCorrectionReason(value), REASON_REQUIRED),
    at: whenSchema,
    lines: z.array(lineSchema).min(1, LINES_REQUIRED).max(MAX_LINES, TOO_MANY_LINES),
  })
  .superRefine((values, ctx) => {
    // The *second* and later mentions are flagged, not the first: the line
    // somebody just picked is the one they are looking at, and reporting the
    // error on line 1 for a mistake made on line 4 sends them to the wrong end
    // of the form.
    const seen = new Set<string>();

    values.lines.forEach((line, index) => {
      if (line.catalogueItemId === '') return;

      if (seen.has(line.catalogueItemId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines', index, 'catalogueItemId'],
          message: DUPLICATE_ITEM,
        });
      }

      seen.add(line.catalogueItemId);
    });
  });

export type CorrectionValues = z.infer<typeof correctionSchema>;

/** A blank line, so "add another" and the form's initial state agree. */
export const blankLine = (): CorrectionValues['lines'][number] => ({
  catalogueItemId: '',
  quantity: '',
});

/**
 * The moment now, in the shape a `datetime-local` field holds, is
 * `localInstantOf` in `lib/local-instant.ts` — beside the parser that reads the
 * same shape back, so the prefill is a value the schema accepts rather than one
 * it happens not to reject. This file had its own copy, written the same day and
 * to the same output by a worker who could not see the other branch; it now
 * imports it. Nothing about the behaviour changed.
 *
 * One attempt at one write — the body it is for, the `id` that body carries and
 * the `Idempotency-Key` that makes sending it twice safe — is `WriteAttempt` and
 * `attemptFor` in `lib/write-attempt.ts`, for the same reason and with the same
 * history. The whole argument for why an editable form needs a third pattern
 * lives there.
 */

/**
 * What this form is about to send, as one string.
 *
 * It covers everything the API fingerprints — `{params, body}` — with the single
 * exception of the body's own `id`, and that exception is not a gap: the id is
 * minted *with* the key by {@link attemptFor} and is therefore a function of this
 * value. Two attempts with equal fingerprints carry the same id as well as the
 * same key, so the raw bodies are identical and the retry replays.
 *
 * `JSON.stringify` over an array rather than over the object, so the comparison
 * cannot turn on key order.
 */
export function fingerprintOf(outletId: string, body: Omit<RecordCorrectionDto, 'id'>): string {
  return JSON.stringify([outletId, body.occurredAt, body.reason, body.lines]);
}
